import React from 'react';
import isEmpty from 'lodash/isEmpty';
import {
  Grid,
  GridRow,
  GridColumn,
  Form,
  Textarea,
  FluidButtonLayout,
  Button,
  Gutter,
  Modal,
  ModalSection,
  ModalHeader,
  ModalFooter,
  Banner,
  CondensedSmall,
  Flashy,
  Layout,
  LayoutItem,
  Paragraph,
  SectionHeader,
} from '@axiom/ui';
import type { Candidate, CandidateProfileIssue } from '@axiom/validation';
import { CandidateSummarySchema } from '@axiom/validation';
import { ProfileCompletionConst } from '@axiom/const';
import { CandidateProfileIssuesUtils } from '@axiom/utils';

import { CandidateApi } from '../../api/candidate-api';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';

import { ProfileSummaryGuidance } from './ProfileSummaryGuidance';

export const ProfileSummaryEdit = ({
  candidate,
  issues,
  onClose,
  name = 'PROFILE_SUMMARY_EDIT_FORM',
}: {
  candidate: Candidate;
  issues: CandidateProfileIssue[];
  onClose: () => void;
  name?: string;
}) => {
  const candidateSummaryLengthEmpty =
    CandidateProfileIssuesUtils.hasSummaryOfAnyLengthIssues(issues);
  const candidateSummaryLengthTooShort =
    CandidateProfileIssuesUtils.hasSummaryOfSufficientLengthIssues(issues);

  return (
    <Form
      name={name}
      schema={CandidateSummarySchema}
      initialValues={{
        candidateSummary: candidate.candidateSummary || '',
      }}
      onSubmit={async formData => {
        // If no changes, just close form
        if (isEmpty(formData)) {
          onClose();
          return;
        }

        await CandidateApi.updateCandidate(candidate.id, formData);
        await CandidateApi.refreshCandidate(candidate.id);
        await CandidateProfileIssuesApi.refreshCandidateProfileIssues(
          candidate.id
        );

        onClose();
      }}
    >
      {({ fireSubmit }) => {
        return (
          <Modal size="large">
            <ModalHeader
              name="PROFILE_SUMMARY_EDIT_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              Edit summary
            </ModalHeader>
            <ModalSection>
              <Grid>
                <GridRow>
                  <GridColumn smallScreenWidth={12} largeScreenWidth={5}>
                    <Layout verticalGutter="16px" wrap>
                      {candidateSummaryLengthEmpty ||
                      candidateSummaryLengthTooShort ? (
                        <LayoutItem fluid>
                          <Banner
                            name="PROFILE_SUMMARY_EDIT_BANNER"
                            type="info"
                            impact="high"
                          >
                            <Layout
                              horizontalGutter="8px"
                              position="middle"
                              wrap
                            >
                              <LayoutItem>
                                <SectionHeader name="PROFILE_SUMMARY_EDIT_BANNER_TITLE">
                                  {candidateSummaryLengthEmpty
                                    ? ProfileCompletionConst
                                        .ProfileCompletionSummaryBanner
                                        .emptyTitle
                                    : null}
                                  {candidateSummaryLengthTooShort
                                    ? ProfileCompletionConst
                                        .ProfileCompletionSummaryBanner
                                        .tooShortTitle
                                    : null}
                                </SectionHeader>
                              </LayoutItem>
                              <LayoutItem>
                                <CondensedSmall>
                                  <Flashy color="textSubtle">|</Flashy>
                                </CondensedSmall>
                              </LayoutItem>
                              <LayoutItem>
                                <CondensedSmall name="PROFILE_SUMMARY_EDIT_BANNER_TIME">
                                  <Flashy color="textSubtle">10 mins</Flashy>
                                </CondensedSmall>
                              </LayoutItem>
                            </Layout>
                            <Gutter bottom="8px" />
                            <Paragraph name="PROFILE_SUMMARY_EDIT_BANNER_CONTENT">
                              {candidateSummaryLengthEmpty
                                ? ProfileCompletionConst
                                    .ProfileCompletionSummaryBanner.emptyContent
                                : null}
                              {candidateSummaryLengthTooShort
                                ? ProfileCompletionConst
                                    .ProfileCompletionSummaryBanner
                                    .tooShortContent
                                : null}
                            </Paragraph>
                          </Banner>
                        </LayoutItem>
                      ) : null}
                      <LayoutItem fluid>
                        <ProfileSummaryGuidance />
                      </LayoutItem>
                    </Layout>
                    <Gutter bottom="16px" only="smallScreen" />
                  </GridColumn>
                  <GridColumn smallScreenWidth={12} largeScreenWidth={7}>
                    <Textarea
                      name="candidateSummary"
                      label="Profile Summary"
                      enableSuggestionsFor="portal-ProfileSummaryEdit-candidateSummary"
                    />
                  </GridColumn>
                </GridRow>
              </Grid>
            </ModalSection>
            <ModalFooter name="PROFILE_SUMMARY_EDIT_FORM_CONTROLS">
              <Gutter bottom="16px" />
              <FluidButtonLayout>
                <Button
                  variation="outline"
                  name="PROFILE_SUMMARY_EDIT_CANCEL"
                  onClick={() => {
                    onClose();
                  }}
                >
                  Cancel
                </Button>
                <Button name="PROFILE_SUMMARY_EDIT_SAVE" onClick={fireSubmit}>
                  Save
                </Button>
              </FluidButtonLayout>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
